import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, ArrowLeft, Menu, X, User, ChevronDown, LogOut, UserCircle, Mail } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'
import type { Department, Program } from '../types'
import { EMPLOYMENT_STATUS_OPTIONS } from '../constants/profileOptions'

const AlumniSignupPage: React.FC = () => {
  const navigate = useNavigate()
  const { register, user, logout } = useAuthStore()
  const [submitting, setSubmitting] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  const getDashboardRoute = () => {
    if (user?.role === 'admin') return '/admin'
    if (user?.role === 'alumni') return '/dashboard'
    if (user?.role === 'employer') return '/employer-dashboard'
    return '/dashboard'
  }

  const handleLogout = () => {
    setIsDropdownOpen(false)
    logout()
  }
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    graduationYear: '',
    department: '',
    program: '',
    employmentStatus: '',
  })
  
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [successMessage, setSuccessMessage] = useState('')
  const [duplicateEmail, setDuplicateEmail] = useState<'confirmed' | 'unconfirmed' | false>(false)
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [departments, setDepartments] = useState<Department[]>([])
  const [allPrograms, setAllPrograms] = useState<Program[]>([])

  useEffect(() => {
    fetchDepartmentsAndPrograms()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch departments and programs from database
  const fetchDepartmentsAndPrograms = async () => {
    try {
      // Fetch departments
      const { data: depts, error: deptsError } = await supabaseClient
        .from('departments')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (deptsError) throw deptsError

      // Fetch programs
      const { data: progs, error: progsError } = await supabaseClient
        .from('programs')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (progsError) throw progsError

      setDepartments(depts || [])
      setAllPrograms(progs || [])
    } catch (err: any) {
      console.error('Error fetching departments and programs:', err)
    }
  }

  // Get available programs based on selected department
  const getProgramsByDepartment = (departmentName: string): Program[] => {
    const dept = departments.find(d => d.name === departmentName)
    if (!dept) return []
    return allPrograms.filter(p => p.department_id === dept.id)
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    if (!formData.graduationYear.trim()) {
      newErrors.graduationYear = 'Graduation year is required'
    } else {
      const year = parseInt(formData.graduationYear)
      const currentYear = new Date().getFullYear()
      if (isNaN(year) || year < 1900 || year > currentYear) {
        newErrors.graduationYear = `Graduation year must be between 1900 and ${currentYear}`
      }
    }
    if (!formData.department.trim()) {
      newErrors.department = 'Department is required'
    }
    if (!formData.program.trim()) {
      newErrors.program = 'Program is required'
    }
    if (!formData.employmentStatus.trim()) {
      newErrors.employmentStatus = 'Employment status is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleResendEmail = async () => {
    if (!successMessage || resending || resendCooldown > 0) return
    setResending(true)
    try {
      const { error } = await supabaseClient.auth.resend({
        type: 'signup',
        email: successMessage,
      })
      if (error) throw error
      toast.success('Verification email resent! Check your inbox.')
      // Start 60-second cooldown to prevent spam
      setResendCooldown(60)
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) { clearInterval(timer); return 0 }
          return prev - 1
        })
      }, 1000)
    } catch (err: any) {
      toast.error('Failed to resend: ' + (err.message || 'Unknown error'))
    } finally {
      setResending(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    if (name === 'email' && duplicateEmail) {
      setDuplicateEmail(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validateForm()) {
      setSubmitting(true)
      try {
        const registered = await register(formData.email, formData.password, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          graduation_year: formData.graduationYear,
          department: formData.department,
          program: formData.program,
          current_employment_status: formData.employmentStatus,
          role: 'alumni'
        })
        if (registered) {
          setSuccessMessage(formData.email)
        }
      } catch (error: any) {
        if (error.message === 'EMAIL_ALREADY_REGISTERED_CONFIRMED') {
          setDuplicateEmail('confirmed')
        } else if (error.message === 'EMAIL_ALREADY_REGISTERED_UNCONFIRMED') {
          setDuplicateEmail('unconfirmed')
        } else {
          setErrors({ email: error.message || 'Registration failed' })
        }
      } finally {
        setSubmitting(false)
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>

            <div className="hidden md:flex">
              {user ? (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                  >
                    Dashboard
                  </button>
                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center space-x-2 focus:outline-none"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center hover:bg-gray-400 transition-colors overflow-hidden">
                        {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                          <img
                            src={user.profile.profile_picture}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/default-avatar.svg'
                            }}
                          />
                        ) : (
                          <User className="w-6 h-6 text-gray-600" />
                        )}
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                          <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                        </div>
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false)
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <UserCircle className="w-4 h-4" />
                          <span>View Profile</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Logout</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link to="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Login
                </Link>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6 text-gray-600" /> : <Menu className="w-6 h-6 text-gray-600" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/news" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>News & Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900 px-2 py-2 hover:bg-gray-50 rounded" onClick={() => setIsMobileMenuOpen(false)}>About UCLM</Link>

                <div className="pt-4 border-t border-gray-200">
                  {user ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          navigate(getDashboardRoute())
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                      >
                        Dashboard
                      </button>
                      <div className="flex items-center justify-between px-2 py-2 bg-gray-50 rounded">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                            {user?.profile && 'profile_picture' in user.profile && user.profile.profile_picture ? (
                              <img
                                src={user.profile.profile_picture}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-avatar.svg'
                                }}
                              />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900 truncate">{user?.email}</p>
                            <p className="text-[11px] text-gray-500 capitalize">{user?.role}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (user?.role === 'admin') {
                              navigate('/admin/profile')
                            } else if (user?.role === 'alumni') {
                              navigate('/profile')
                            } else if (user?.role === 'employer') {
                              navigate('/employer/profile')
                            }
                            setIsMobileMenuOpen(false)
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          handleLogout()
                          setIsMobileMenuOpen(false)
                        }}
                        className="w-full text-gray-700 hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors text-sm flex items-center justify-center space-x-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-center"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="text-center mb-8">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">Trailblaze</div>
            <p className="text-sm sm:text-base text-gray-600">Create Alumni Account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              {duplicateEmail && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                  <p className="font-medium">This email is already registered.</p>
                  {duplicateEmail === 'unconfirmed' ? (
                    <>
                      <p className="mt-1 mb-3">
                        You registered but haven't confirmed your email yet. Resend the verification link below, or{' '}
                        <Link to="/login" className="font-semibold text-blue-600 hover:underline">log in here</Link>.
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          if (resending || resendCooldown > 0) return
                          setResending(true)
                          try {
                            const { error } = await supabaseClient.auth.resend({
                              type: 'signup',
                              email: formData.email,
                            })
                            if (error) throw error
                            toast.success('Verification email resent! Check your inbox.')
                            setResendCooldown(60)
                            const timer = setInterval(() => {
                              setResendCooldown((prev) => {
                                if (prev <= 1) { clearInterval(timer); return 0 }
                                return prev - 1
                              })
                            }, 1000)
                          } catch (err: any) {
                            toast.error('Failed to resend: ' + (err.message || 'Unknown error'))
                          } finally {
                            setResending(false)
                          }
                        }}
                        disabled={resending || resendCooldown > 0}
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Verification Email'}
                      </button>
                    </>
                  ) : (
                    <p className="mt-1">
                      Please use a different email or{' '}
                      <Link to="/login" className="font-semibold text-blue-600 hover:underline">log in here</Link>.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Graduation Year
              </label>
              <select
                name="graduationYear"
                value={formData.graduationYear}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.graduationYear ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select graduation year</option>
                {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.graduationYear && (
                <p className="mt-1 text-sm text-red-600">{errors.graduationYear}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                College/Department
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={(e) => {
                  const newDepartment = e.target.value
                  setFormData((prev) => ({ ...prev, department: newDepartment, program: '' }))
                  if (errors.department) {
                    setErrors((prev) => ({ ...prev, department: undefined }))
                  }
                }}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.department ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select college/department</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.name}>
                    {dept.name}
                  </option>
                ))}
              </select>
              {errors.department && (
                <p className="mt-1 text-sm text-red-600">{errors.department}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program
              </label>
              <select
                name="program"
                value={formData.program}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.program ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={!formData.department}
              >
                <option value="">Select program</option>
                {getProgramsByDepartment(formData.department).map((program) => (
                  <option key={program.id} value={program.code}>
                    {program.full_name || program.name}
                  </option>
                ))}
              </select>
              {errors.program && (
                <p className="mt-1 text-sm text-red-600">{errors.program}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employment Status
              </label>
              <select
                name="employmentStatus"
                value={formData.employmentStatus}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.employmentStatus ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select employment status</option>
                {EMPLOYMENT_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {errors.employmentStatus && (
                <p className="mt-1 text-sm text-red-600">{errors.employmentStatus}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {!successMessage && (
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creating Account...' : 'Create Account'}
              </button>
            )}
          </form>

          {successMessage && (
            <div className="text-center space-y-4 py-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Mail className="w-9 h-9 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900">Check Your Email</h2>
              <p className="text-sm text-gray-600">
                We sent a verification link to <span className="font-semibold text-gray-800">{successMessage}</span>.
                Click the link in the email to confirm your account.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">What happens next?</h3>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Verify your email by clicking the link we sent</li>
                  <li>Log in to your account</li>
                  <li>Wait for your department admin to approve your registration</li>
                  <li>Once approved, you'll have full access to Trailblaze</li>
                </ol>
              </div>
              <button
                onClick={handleResendEmail}
                disabled={resending || resendCooldown > 0}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {resending
                  ? 'Sending...'
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Didn't receive it? Resend email"}
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm sm:text-base text-blue-600 hover:underline">
              Back to Login
            </Link>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            Back to Home
          </Link>
        </div>
      </div>
      </div>
    </div>
  )
}

export default AlumniSignupPage
